/**
 * Custom PromiseCancelledError Error type
 * @class PromiseCancelledError
 */
export class PromiseCancelledError extends Error {

	static NAME = 'PromiseCancelledError';

	/**
	 * @constructor
	 * @param {string=} message
	 */
	constructor(message) {
		super(message || 'ERR_PROMISE_CANCELLED');
		// eslint-disable-next-line unicorn/custom-error-definition
		this.name = PromiseCancelledError.NAME;
	}

	toString() {
		/** @type {any} */
		const ctr = this.constructor;
		return `${ctr.NAME}: ${this.message}`;
	}
}

/**
 * @typedef {{
 *  resolve: (value: any) => void;
 *  reject: (value: any) => void;
 *  cancel: (err: any) => void;
 *  exec: (cb: function) => Deferred;
 *  done: (...fns: function[]) => Deferred;
 *  fail: (...fns: function[]) => Deferred;
 *  always: (...fns: function[]) => Deferred;
 *  then: (onFulfilled?: function, onRejected?: function) => Promise;
 *  catch: (onRejected?: function) => Promise;
 *  finally: (onFinally: function) => Promise;
 *  fulfilled: boolean;
 *  cancelled: boolean;
 *  state: 'pending'|'resolved'|'rejected';
 *  result: any;
 * }} Deferred
 */

// const isObject = (it) => it !== null && typeof it === 'object';
// const isCallable = (it) => typeof it === 'function';
// const isThenable = (it) => {
// 	let then;
// 	// eslint-disable-next-line prefer-destructuring
// 	return isObject(it) && isCallable(then = it.then) ? then : false;
// };

/**
 * @param {function} def
 * @returns {Deferred}
 */
export default function Deferred(def = (res, rej) => {}) {
	let resolve;
	let reject;
	let result;
	let state = 'pending';
	let isResolving = false;
	let cancelled = false;
	const cbs = [];
	// const eventCbs = [];
	// const onResolved = fn => {
	// 	eventCbs.push({ fn, type: 2 });
	// };

	// TODO: Allow chaining of methods then/catch/finally without losing the original promise
	// https://medium.com/@masnun/creating-cancellable-promises-33bf4b9da39c
	// https://github.com/wojtekmaj/make-cancellable-promise
	// https://github.com/srmagura/real-cancellable-promise
	// https://github.com/alkemics/CancelablePromise
	// https://github.com/alkemics/CancelablePromise/blob/master/src/CancelablePromise.ts

	const p = new Promise((res, rej) => {

		/**
		 * @param {any} value
		 */
		resolve = async (value) => {

			if (state !== 'pending' || isResolving) {
				return;
			}

			// This seems to be PromiseLike interface
			if (value && value.then) {
				isResolving = true;
				try {
					result = await value;
					// eslint-disable-next-line require-atomic-updates
					isResolving = false;
				}
				catch (error) {
					// eslint-disable-next-line require-atomic-updates
					isResolving = false;
					reject(error);
					return;
				}
			}
			else {
				result = value;
			}

			// eslint-disable-next-line require-atomic-updates
			state = 'resolved';

			// Now we resolve the promise
			res(result);

			// Call all then/catch/finally callbacks
			// while (eventCbs.length > 0) {
			// 	const cb = eventCbs.shift();
			// 	if (cb.type > 0) {
			// 		cb.fn(result);
			// 	}
			// }

			// Call all done/always callbacks
			while (cbs.length > 0) {
				const cb = cbs.shift();
				if (cb.type > 0) {
					cb.fn(result);
				}
			}
		};

		/**
		 * @param {any} value
		 */
		reject = async (value) => {

			if (state !== 'pending' || isResolving) {
				return;
			}

			// This seems to be PromiseLike
			if (value && value.then) {
				isResolving = true;
				try {
					result = await value;
				}
				catch {
					result = value;
				}
				finally {
					// eslint-disable-next-line require-atomic-updates
					isResolving = false;
				}
			}
			else {
				result = value;
			}

			// eslint-disable-next-line require-atomic-updates
			state = 'rejected';

			// Now we reject the promise
			rej(result);

			// Call all fail/always callbacks
			while (cbs.length > 0) {
				const cb = cbs.shift();
				if (cb.type < 2) {
					cb.fn(result);
				}
			}
		};

		def(resolve, reject);
	});

	// The Deferred object
	/** @type {Deferred} */
	// @ts-ignore
	const D = Object.assign(p, {
		resolve,
		reject,

		/**
		 * @param {any} err
		 */
		cancel(err) {
			cancelled = true;
			reject(err || new PromiseCancelledError());
		},

		/**
		 * @param {function?} fn
		 */
		exec(fn) {
			fn && fn.call(D, D);
			return D;
		},

		/**
		* @param  {...function} fns
		*/
		done(...fns) {
			if (state === 'resolved') {
				for (const fn of fns) {
					fn && fn(result);
				}
			}
			else if (state === 'pending') {
				cbs.push(...fns.filter(Boolean).map(v => ({ fn: v, type: 2 })));
			}
			return D;
		},

		/**
		* @param  {function} onFulfilled
		* @param  {function} onRejected
		*/
		// eslint-disable-next-line unicorn/no-thenable
		// then(onFulfilled, onRejected) {

		// 	/** @type {Deferred} */
		// 	const DD = Deferred();
		// 	// DD.done(onFulfilled).fail(onRejected);

		// 	onFulfilled((value) => {
		// 		DD.resolve(value);
		// 	});

		// 	onRejected((value) => {
		// 		DD.reject(value);
		// 	});

		// 	return DD;
		// },

		/**
		* @param  {...function} fns
		*/
		fail(...fns) {
			if (state === 'rejected') {
				for (const fn of fns) {
					fn && fn(result);
				}
			}
			else if (state === 'pending') {
				cbs.push(...fns.filter(Boolean).map(v => ({ fn: v, type: 0 })));
			}
			return D;
		},

		/**
		* @param  {function} onRejected
		*/
		// catch(onRejected) {
		// 	if (state === 'rejected') {
		// 		for (const fn of fns) {
		// 			fn(result);
		// 		}
		// 	}
		// 	else if (state === 'pending') {
		// 		return D.fail(...fns);
		// 	}
		// 	return D;
		// },

		/**
		* @param  {...function} fns
		*/
		always(...fns) {
			if (state !== 'pending') {
				for (const fn of fns) {
					fn && fn(result);
				}
			}
			else {
				cbs.push(...fns.filter(Boolean).map(v => ({ fn: v, type: 1 })));
			}
			return D;
		},

		/**
		* @param  {...function} fns
		*/
		// finally(...fns) {
		// 	return Deferred.create();
		// },
	});

	// ------------------------
	// Expand getters & methods
	// ------------------------

	Object.defineProperties(D, {
		fulfilled: {
			get() {
				return state !== 'pending';
			},
		},
		cancelled: {
			get() {
				return cancelled;
			},
		},
		state: {
			get() {
				return state;
			},
		},
		result: {
			get() {
				return result;
			},
		},
	});

	return D;
}

/**
 * @param {any} value
 * @returns {Deferred}
 */
Deferred.resolve = (value) => {
	return Deferred(resolve => resolve(value));
};

/**
 * @param {any} value
 * @returns {Deferred}
 */
Deferred.reject = (value) => {
	return Deferred((_, reject) => reject(value));
};

/**
 * @param {((def: Deferred) => void)=} cb
 * @returns {Deferred}
 */
Deferred.create = (cb) => {
	return Deferred().exec(cb);
};

// @ts-ignore
// window.Deferred = Deferred;

// ---------------------------------------------------------------------------------
// ---------------------------------------------------------------------------------
// ---------------------------------------------------------------------------------

/**
 * Custom DeferredTaskError Error type
 */
// export class DeferredTaskError extends Error {

// 	static NAME = 'DeferredTaskError';

// 	/**
// 	 * @constructor
// 	 * @param {string=} message
// 	 */
// 	constructor(message) {
// 		super(message || 'ERR_DEFERRED_TASK_MISSING');
// 	}

// 	toString() {
// 		/** @type {any} */
// 		const ctr = this.constructor;
// 		return `${ctr.NAME}: ${this.message}`;
// 	}
// }

// let d = Deferred();
// d.then(value => console.log('Hello', value));
// d.resolve('World');

/**
 * https://github.com/nodejs/node/issues/13678
 * https://github.com/nodejs/node/issues/13678#issuecomment-326825605
 * https://github.com/nodejs/node/issues/13678#issuecomment-352625977
 * https://github.com/nodejs/node/issues/13678#issuecomment-660557146
 * https://gist.github.com/oliverfoster/00897f4552cef64653ef14d8b26338a6
 */
// export default class DeferredPromise extends Promise {

// 	#state = 'pending';
// 	#result;
// 	#cancelled = false;

// 	/** @type {function} */
// 	#task;

// 	/** @type {{fn: function, type: number}[]} */
// 	#callbacks = [];

// 	/**
// 	 * @param {function} task
// 	 */
// 	static create(task) {
// 		return (new this).exec(task);
// 	}

// 	// @ts-ignore
// 	constructor(def = (res, rej) => { }) {

// 		let res, rej;
// 		super((resolve, reject) => {
// 			def(resolve, reject);
// 			res = resolve;
// 			rej = reject;
// 		});

// 		// Must set these props after the super call
// 		/** @type {function(any): void} */
// 		this.resolve = res;
// 		/** @type {function(any): void} */
// 		this.reject = rej;

// 		// Call done/fail/always
// 		this.then(result => {
// 			this.#state = 'fulfilled';
// 			this.#result = result;

// 			const cbs = this.#callbacks;
// 			while (cbs.length > 0) {
// 				const cb = cbs.shift();
// 				if (cb.type > 0) {
// 					cb.fn(result);
// 				}
// 			}
// 			return result;
// 		}, result => {
// 			this.#state = 'rejected';
// 			this.#result = result;

// 			const cbs = this.#callbacks;
// 			while (cbs.length > 0) {
// 				const cb = cbs.shift();
// 				if (cb.type < 2) {
// 					cb.fn(result);
// 				}
// 			}
// 			// Note: if we add throw here, the reject call will always throw an 'Uncaught (in promise)'
// 			// throw result;
// 			return Promise.reject(result);
// 		});
// 		// Note: if we add a finally here, the reject call will always throw an 'Uncaught (in promise)'
// 		// this.finally(() => {
// 		// 	console.log('finally');
// 		// });
// 	}

// 	get isCancelled() {
// 		return this.#cancelled;
// 	}

// 	cancel(err) {
// 		this.#cancelled = true;
// 		this.reject(err || new PromiseCancelledError());
// 	}

// 	exec(fn) {
// 		try {
// 			fn.call(this, this);
// 		}
// 		catch (e) {
// 			this.reject(e);
// 		}
// 		return this;
// 	}

// 	/**
// 	 * @param {function} task
// 	 */
// 	defer(task) {
// 		this.#task = task;
// 		return this;
// 	}

// 	/**
// 	 * @param {any[]} args
// 	 */
// 	async execute(...args) {

// 		if (this.#state === 'pending') {

// 			const task = this.#task;

// 			if (!task) {
// 				this.reject(new DeferredTaskError());
// 			}
// 			else {
// 				try {
// 					args.push(this);
// 					const value = await task.call(this, ...args);
// 					this.resolve(value);
// 				}
// 				catch (err) {
// 					this.reject(err);
// 				}
// 				finally {
// 					this.#task = undefined;
// 				}
// 			}
// 		}
// 	}

// 	/**
// 	 * @param  {...function} fns
// 	 */
// 	done(...fns) {
// 		if (this.#state === 'fulfilled') {
// 			for (const fn of fns) {
// 				fn(this.#result);
// 			}
// 		}
// 		else if (this.#state === 'pending') {
// 			this.#callbacks.push(...fns.map(v => ({ fn: v, type: 2 })));
// 		}
// 		return this;
// 	}

// 	/**
// 	 * @param  {...function} fns
// 	 */
// 	fail(...fns) {
// 		if (this.#state === 'rejected') {
// 			for (const fn of fns) {
// 				fn(this.#result);
// 			}
// 		}
// 		else if (this.#state === 'pending') {
// 			this.#callbacks.push(...fns.map(v => ({ fn: v, type: 0 })));
// 		}
// 		return this;
// 	}

// 	/**
// 	 * @param  {...function} fns
// 	 */
// 	always(...fns) {
// 		if (this.#state !== 'pending') {
// 			for (const fn of fns) {
// 				fn(this.#result);
// 			}
// 		}
// 		else {
// 			this.#callbacks.push(...fns.map(v => ({ fn: v, type: 1 })));
// 		}
// 		return this;
// 	}

// 	// You can use Symbol.species in order to return a Promise for then/catch/finally
// 	// Note: if we return a DeferredPromise constructor here, then the other callbacks would have access to it
// 	// Note2: But if we return a Promise constructor here, the DeferredPromise executor will not be called twice on initialization
// 	static get [Symbol.species]() {
// 		return Promise;
// 	}

// 	// Promise overrides its Symbol.toStringTag
// 	get [Symbol.toStringTag]() {
// 		return 'DeferredPromise';
// 	}
// }

// // @ts-ignore
// window.DeferredPromise = DeferredPromise;

// Create a constructor that uses `Promise` as its super and does the `super` call
// via `Reflect.construct`
/**
 * @class MyPromise
 */
// export const MyPromise = function (executor) {
// 	this._exResolve = null;
// 	this._exReject = null;
// 	this._fulfilled = false;

// 	const extendedExecutor = (resolve, reject) => {
// 		const res = val => {
// 			if (!this._fulfilled) {
// 				resolve(val);
// 			}
// 			this._fulfilled = true;
// 		};
// 		const rej = val => {
// 			if (!this._fulfilled) {
// 				reject(val);
// 			}
// 			this._fulfilled = true;
// 		};
// 		this._exResolve = res;
// 		this._exReject = rej;
// 		return executor(res, rej);
// 	};
// 	return Reflect.construct(Promise, [extendedExecutor], MyPromise);
// };
// // Make `MyPromise` inherit statics from `Promise`
// Object.setPrototypeOf(MyPromise, Promise);
// // Create the prototype, add methods to it
// MyPromise.prototype = Object.create(Promise.prototype);
// MyPromise.prototype.constructor = MyPromise;
// MyPromise.prototype.resolve = function (value) { this._exResolve(value); };
// MyPromise.prototype.reject = function (value) { this._exReject(value); };
// // @ts-ignore
// window.MyPromise = MyPromise;

// const fn1 = async () => {
// 	const p1 = new DeferredPromise((resolve, reject) => {
// 		setTimeout(() => void resolve(100), 1000);
// 	});

// 	try {

// 		const p1then = p1.then(v => { console.log(`Resolved with: ${v}`); throw new Error(String(v * 2)); }, v => console.warn(`Rejected 1 with: ${v}`))/* .catch(e => { console.log('caught error', e); return 'caught'; }) */;
// 		const p1catch = p1then.then(null, e => { console.log('caught error', e); return 'caught the p1then'; });

// 		p1catch.catch(v => { console.warn(`Rejected 3 with: ${v}`); return 'caught in p1catch'; });
// 		p1.then(v => console.log(`Resolved 2 with: ${v}`), v => { console.warn(`Rejected 2 with: ${v}`); return 'caught in p1catch'; });
// 		// p1then.then(v => console.log(`Resolved 3 with: ${v}`));

// 		setTimeout(() => p1.resolve(500), 500);
// 		setTimeout(() => p1.resolve(750), 750);

// 		// console.log('p1', p1);
// 		// console.log('p1then', p1then);
// 		// console.log('p1catch', p1catch);
// 		// console.log('p1 === p1then', p1 === p1then);
// 		// console.log('p1 === p1catch', p1 === p1catch);
// 		// console.log('p1then === p1catch', p1then === p1catch);
// 	}
// 	catch (e) {
// 		console.log('caught in block', e);
// 	}
// };

// window.addEventListener('unhandledrejection', function (event) {
// 	// the event object has two special properties:
// 	console.log('event.promise', event.promise); // [object Promise] - the promise that generated the error
// 	console.log('event.reason', event.reason); // Error: Whoops! - the unhandled error object
// });

// (async () => {
// 	await fn1();
// })();